import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import AppointmentUtils from 'services/AppointmentUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CalendarUtils from 'services/CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import RouteUtils from 'services/RouteUtils';
import View from './view';
import useFetchPatients from 'hooks/useFetchPatients';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import _ from 'lodash';
import { IsTypeNurse, IsTypePhlebotomist } from 'careand-redux/selectors';
import ShiftUtils from 'services/ShiftUtils';
import { PERMISSIONS } from 'services/StaffUtils';

const TODAY_UNIX = CalendarUtils.convertFromTargetTimezone(moment()).startOf('day').unix();

const DEFAULT_ARRAY = [];

const Schedule = (props) => {
  const { dontLoadDayAppointments, onEventClick, fetchShifts, dontLoadPatients, clinicId } = props;

  const nav = useNavStack(Stacks.Master);
  const dispatch = useAppDispatch();

  const [selectedDate, setSelectedDate] = useState(moment.unix(props.showDay || TODAY_UNIX));
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState([]);
  const [events, setEvents] = useState({});

  const permissions = useAppSelector((state) => state.authentication.permissions);
  const handovers = useAppSelector((state) => state.shifts.handovers);
  const bubbles = useAppSelector((state) => state.ui.bubbles);
  const selectorPhysicians = useAppSelector((state) => state.physicians.byId);
  const patients = useAppSelector((state) => state.patients.byId);
  const selectorAppointments = useAppSelector((state) => state.appointments.byId);
  const authentication = useAppSelector((state) => state.authentication);
  const selectorShifts = useAppSelector((state) => state.shifts.shifts);
  const editedAppointmentEvents = useAppSelector((state) => state.editedAppointmentEvents);

  const isPhlebotomist = useAppSelector(IsTypePhlebotomist);
  const isShiftManager = permissions.includes(PERMISSIONS.shift_manager);

  // Every schedule snaps to 5 minute slots (the granularity lab appointments
  // have always used), so appointments can start off the quarter hour.
  const slotInterval = 5;

  const physicians = useMemo(() => {
    let physicians = [];

    if (!props.physician) {
      physicians = PerformanceUtils.objectToArray(selectorPhysicians);
    } else {
      physicians.push(props.physician);
    }

    return physicians;
  }, [selectorPhysicians, props.physician]);

  const clinicIds = useMemo(() => {
    return clinicId ? [clinicId] : _.uniq(physicians.map((physician) => physician.clinic_id));
  }, [physicians, clinicId]);

  const allSelected = useMemo(() => {
    return (
      physicians.length === 0 ||
      PerformanceUtils.objectToArray(selectorPhysicians).length === physicians.length
    );
  }, [physicians, selectorPhysicians]);

  const shifts = useMemo(() => {
    let shifts = CalendarUtils.filterShifts(selectorShifts, selectedDate, clinicId);
    let ids = physicians.map((physician) => physician.id);
    return shifts.filter((shift) => ids.includes(shift.user_id));
  }, [selectedDate, selectorShifts, physicians, clinicId]);

  const selectedDateUnix = selectedDate.unix();

  useEffect(() => {
    if (fetchShifts) {
      clinicIds.forEach((clinicId) => {
        dispatch(
          api.Shifts.getDayShifts({
            clinicId,
            date: selectedDateUnix,
          }),
        );
      });
    }
  }, [dispatch, fetchShifts, selectedDateUnix, clinicIds]);

  useEffect(() => {
    if (!dontLoadDayAppointments) {
      clinicIds.forEach((clinicId) => {
        dispatch(
          api.Appointments.getDayAppointments({
            date: moment().unix(),
            clinicId,
          }),
        );
      });
    }
  }, [dontLoadDayAppointments, dispatch, clinicIds]);

  useEffect(() => {
    let date = props.showDay || moment().unix();
    date = CalendarUtils.convertToTargetTimezone(moment.unix(date));
    date = date.startOf('day');
    setSelectedDate(date);
  }, [props.showDay]);

  useEffect(() => {
    let events;

    events = getEvents(
      physicians,
      appointments,
      patients,
      selectorPhysicians,
      editedAppointmentEvents,
      props.physician !== undefined ? shifts : undefined,
      props.showActualTime,
      handovers,
    );

    setEvents(events);
  }, [
    props.physician,
    selectorPhysicians,
    physicians,
    appointments,
    patients,
    editedAppointmentEvents,
    shifts,
    props.showActualTime,
    handovers,
  ]);

  useEffect(() => {
    let appointments = AppointmentUtils.getScheduleAppointments(
      selectorAppointments,
      physicians,
      selectedDate,
      props.showCancelled,
      clinicId,
    );

    if (!isShiftManager) {
      appointments = appointments.filter(
        ({ complaints, type }) =>
          !(type === AppointmentUtils.getAppointmentTypes().blocked && complaints === 'HIDDEN'),
      );
    }

    setAppointments(appointments);
  }, [
    allSelected,
    selectedDate,
    patients,
    editedAppointmentEvents,
    selectorAppointments,
    physicians,
    selectorPhysicians,
    props.showCancelled,
    isShiftManager,
    clinicId,
  ]);

  useFetchPatients(dontLoadPatients ? DEFAULT_ARRAY : appointments);

  function closeModal() {
    setSelectedAppointmentId(undefined);
  }

  function getSelectedAppointment() {
    if (!selectedAppointmentId) return undefined;
    return appointments.filter((appointment) => appointment.id === selectedAppointmentId)[0];
  }

  function clickedAppointment(appointment) {
    if (appointment.type === AppointmentUtils.getAppointmentTypes().blocked) {
      setSelectedAppointmentId(appointment.id);
      return;
    }

    if (onEventClick) return onEventClick(appointment);

    if (authentication.role === 'admin' || isPhlebotomist) {
      setSelectedAppointmentId(appointment.id);
    } else if (authentication.role === 'physician') {
      let route;

      let appointmentBubble = bubbles.filter(
        (bubble) => bubble.appointment_id === appointment.id,
      )[0];
      let patientBubble = bubbles.filter(
        (bubble) => bubble.patient_id === appointment.patient_id,
      )[0];

      let bubble = appointmentBubble || patientBubble;

      if (bubble) {
        route = RouteUtils.getRoute(RouteUtils.routes().bubble, {
          ':bubbleId': bubble.id,
        });
      } else {
        route = RouteUtils.getRoute(RouteUtils.routes().patient, {
          ':patientId': appointment.patient_id,
        });
      }

      nav.push(route);
    }
  }

  function onArrowClick() {
    nav.push(RouteUtils.getRoute(RouteUtils.routes().schedule));
  }

  function onDateSelect(date) {
    date = moment(date);
    setSelectedDate(date);
    dispatch(api.Appointments.getDayAppointments({ date: date.unix() }));
    if (props.onChangeDay !== undefined) props.onChangeDay(date.unix());
  }

  function moveEvent({ event, start, end }) {
    if (props.disableDragNDrop) return;

    if (event.type === 'appointment') {
      let appointment = event.appointment;

      if (AppointmentUtils.isBlockedSlot(appointment) && authentication.role === 'physician')
        return;

      start = CalendarUtils.convertToTargetTimezone(start);
      updateEvent(event, start, end);

      let dateUnix = moment(start).unix();
      let date_end = undefined;

      if (appointment.date_end) {
        let duration = AppointmentUtils.getDuration(appointment);
        date_end = AppointmentUtils.calculateEndDate({ ...appointment, date: dateUnix }, duration);
      }

      let updatedAppointment = {
        ...appointment,
        date: dateUnix,
        date_end,
      };

      let newAppointments = PerformanceUtils.editOrAdd(updatedAppointment, appointments);
      setAppointments(newAppointments);

      dispatch(
        api.Appointments.updateAppointment({
          confirmSlot: true,
          appointment: updatedAppointment,
        }),
        {
          errorTitle: 'Appointment Update',
        },
      ).then((appointment) => {
        if (!appointment) {
          setAppointments(appointments);
          updateEvent(event, event.start, event.end);
        }
      });
    }
  }

  function updateEvent(event, start, end) {
    const idx = events.events.indexOf(event);
    const updatedEvent = { ...event, start, end };
    let nextEvents = [...events.events];
    nextEvents.splice(idx, 1, updatedEvent);
    nextEvents = { ...events, events: [...nextEvents] };
    setEvents(nextEvents);
  }

  return (
    <View
      {...props}
      selectedDate={selectedDate}
      appointments={appointments}
      selectedAppointmentId={selectedAppointmentId}
      events={events}
      shifts={shifts}
      physicians={physicians}
      clickedAppointment={clickedAppointment}
      onDateSelect={onDateSelect}
      moveEvent={props.disableDragNDrop ? undefined : props.moveEvent || moveEvent}
      selectedAppointment={getSelectedAppointment()}
      closeModal={closeModal}
      onArrowClick={onArrowClick}
      step={slotInterval}
    />
  );
};

Schedule.propTypes = {
  physician: PropTypes.object,
  showCalendar: PropTypes.bool,
  agendaTitle: PropTypes.string,
  showAgenda: PropTypes.bool,
  showAgendaToolbar: PropTypes.bool,
  showArrow: PropTypes.bool,
  showDay: PropTypes.number,
  disableDragNDrop: PropTypes.bool,
  onChangeDay: PropTypes.func,
  moveEvent: PropTypes.func,
  onEventClick: PropTypes.func,
  onSelectSlot: PropTypes.func,
  dontLoadDayAppointments: PropTypes.bool,
  dontLoadPatients: PropTypes.bool,
};

Schedule.defaultProps = {
  showCancelled: false,
  dontLoadPatients: false,
  dontLoadDayAppointments: false,
};

export default Schedule;

function getEvents(
  selectedPhysicians,
  appointments,
  patients,
  physicians,
  editedAppointmentEvents,
  shifts = [],
  showActualTime,
  activeHandovers = [],
) {
  editedAppointmentEvents.forEach((editedAppointmentEvent) => {
    let editedAppointment = editedAppointmentEvent.appointment;
    let physicianIds = selectedPhysicians.map((physician) => physician.id);

    if (physicianIds.includes(editedAppointment.physician_id)) {
      appointments = PerformanceUtils.editOrAdd(editedAppointment, appointments);
    } else {
      appointments = PerformanceUtils.removeFromArray(editedAppointment, appointments);
    }
  });

  let appointmentEvents = AppointmentUtils.appointmentsToEvents(
    appointments,
    patients,
    physicians,
    showActualTime,
  );

  let processedShifts = processShifts(shifts);
  let shiftEvents = ShiftUtils.shiftsToEvents(processedShifts, physicians, activeHandovers);

  return { shifts: shiftEvents, events: appointmentEvents };
}

function processShifts(shifts = []) {
  let result = [];

  shifts.forEach((shift) => {
    let { breaks = [] } = shift;

    function convertMilitary(hour) {
      return CalendarUtils.militaryTimeToUnix(shift.start, hour);
    }

    // If there are no breaks, add the entire shift to result
    if (breaks.length === 0) {
      result.push(shift);
    } else {
      // Sort the breaks by their start time in ascending order
      let sortedBreaks = [...breaks];
      sortedBreaks.sort((a, b) => convertMilitary(a.start_hour) - convertMilitary(b.start_hour));

      // Create the first block from shift start to the start of the first break
      result.push({
        ...shift,
        end: convertMilitary(sortedBreaks[0].start_hour),
        id: shift.id + 1,
      });

      // Create the blocks for breaks and between breaks
      for (let i = 0; i < sortedBreaks.length; i++) {
        // Create block for break
        result.push({
          ...shift,
          start: convertMilitary(sortedBreaks[i].start_hour),
          end: convertMilitary(sortedBreaks[i].end_hour),
          id: shift.id + (i + 1) + 'b', // Appending 'b' to indicate break
          non_working: true,
          non_working_reason: sortedBreaks[i].name,
        });

        // Create block after break, unless it's the last break
        if (i < sortedBreaks.length - 1) {
          result.push({
            ...shift,
            start: convertMilitary(sortedBreaks[i].end_hour),
            end: convertMilitary(sortedBreaks[i + 1].start_hour),
            id: shift.id + (i + 2),
          });
        }
      }

      // Create the last block from the end of the last break to shift end
      result.push({
        ...shift,
        start: convertMilitary(sortedBreaks[sortedBreaks.length - 1].end_hour),
        id: shift.id + (sortedBreaks.length + 1),
      });
    }
  });

  result = result.filter(({ start, end }) => start !== end);

  return result;
}
